import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Alert, CircularProgress, Chip,
  Divider, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon   from '@mui/icons-material/ArrowBack';
import SaveIcon        from '@mui/icons-material/Save';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PrintIcon       from '@mui/icons-material/Print';
import ScienceIcon     from '@mui/icons-material/Science';
import { useNavigate } from 'react-router-dom';
import { nitrateNService } from '../services/api';
import { NitrateNDailyReport } from '../types';

// ── constants ────────────────────────────────────────────────────────────────
const FERMENTERS = ['F1', 'F2', 'F3', 'IST', 'MIXING PIT'] as const;
type Fermenter = typeof FERMENTERS[number];

/** ppm NO₃-N = ppm NO₃⁻ × 0.226 */
const calcPpmNo3N = (ppmNo3: number | null): number | null =>
  ppmNo3 == null ? null : +(ppmNo3 * 0.226).toFixed(4);

const calcRemark = (ppmNo3N: number | null) => {
  if (ppmNo3N == null) return null;
  if (ppmNo3N < 50)   return 'Beneficial';
  if (ppmNo3N <= 200) return 'Moderate range';
  return 'Above range';
};

const remarkColor = (r: string | null) => {
  if (r === 'Beneficial')    return 'success';
  if (r === 'Moderate range') return 'warning';
  if (r === 'Above range')   return 'error';
  return 'default';
};

const toN = (s: string) => { const n = parseFloat(s); return isNaN(n) ? null : n; };

// ── component ─────────────────────────────────────────────────────────────────
const NitrateNForm: React.FC = () => {
  const today    = new Date().toISOString().split('T')[0];
  const navigate = useNavigate();

  const [date,         setDate]         = useState(today);
  const [rows,         setRows]         = useState<Record<Fermenter, string>>(
    () => Object.fromEntries(FERMENTERS.map((f) => [f, ''])) as Record<Fermenter, string>,
  );
  const [saving,       setSaving]       = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [dates,        setDates]        = useState<string[]>([]);
  const [savedReport,  setSavedReport]  = useState<NitrateNDailyReport | null>(null);
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState<string | null>(null);

  // ── computed live preview ────────────────────────────────────────────────
  const computed = FERMENTERS.map((ferm) => {
    const ppmNo3  = toN(rows[ferm]);
    const ppmNo3N = calcPpmNo3N(ppmNo3);
    return { ferm, ppmNo3, ppmNo3N, remark: calcRemark(ppmNo3N) };
  });

  // ── fetch available dates ────────────────────────────────────────────────
  const fetchDates = useCallback(async () => {
    setLoadingDates(true);
    try {
      const d = await nitrateNService.getAvailableDates();
      setDates(d);
    } catch { setDates([]); }
    finally { setLoadingDates(false); }
  }, []);

  useEffect(() => { fetchDates(); }, [fetchDates]);

  // ── load existing data when date changes ─────────────────────────────────
  const loadDate = useCallback(async (d: string) => {
    if (!d) return;
    try {
      const report: NitrateNDailyReport = await nitrateNService.getDailyReport(d);
      setSavedReport(report);
      const newRows = Object.fromEntries(FERMENTERS.map((f) => [f, ''])) as Record<Fermenter, string>;
      for (const fe of report.fermenters) {
        if (fe.ppmNo3 != null && FERMENTERS.includes(fe.fermenter as Fermenter)) {
          newRows[fe.fermenter as Fermenter] = String(fe.ppmNo3);
        }
      }
      setRows(newRows);
    } catch {
      setSavedReport(null);
      setRows(Object.fromEntries(FERMENTERS.map((f) => [f, ''])) as Record<Fermenter, string>);
    }
  }, []);

  useEffect(() => { loadDate(date); }, [date, loadDate]);

  // ── save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const entries = FERMENTERS
        .filter((f) => rows[f].trim() !== '')
        .map((f) => ({ measurementDate: date, fermenter: f, ppmNo3: toN(rows[f]) }));

      if (entries.length === 0) {
        setError('Enter at least one ppm NO₃⁻ value.');
        return;
      }
      await nitrateNService.upsertBatch(entries);
      setSuccess(`Saved ${entries.length} entry(ies) for ${date}.`);
      fetchDates();
      loadDate(date);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── delete ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm(`Delete all Nitrate-N entries for ${date}?`)) return;
    try {
      await nitrateNService.deleteByDate(date);
      setRows(Object.fromEntries(FERMENTERS.map((f) => [f, ''])) as Record<Fermenter, string>);
      setSavedReport(null);
      setSuccess(`Deleted entries for ${date}.`);
      fetchDates();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Delete failed');
    }
  };

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #388e3c 100%)',
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
        justifyContent: 'space-between', flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/lab-report')} sx={{ color: '#fff', mr: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <ScienceIcon sx={{ fontSize: '2rem', opacity: 0.9 }} />
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.8, lineHeight: 1, letterSpacing: 2, display: 'block' }}>
              POLOMOLOK — CHEMICAL LAB
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Nitrate-Nitrogen (NO₃-N)
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Print">
            <IconButton onClick={() => window.print()} sx={{ color: '#fff' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Date picker + available dates */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Measurement Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            size="small"
            inputProps={{ max: today }}
            sx={{ minWidth: 180 }}
          />
          {loadingDates && <CircularProgress size={18} />}
          {dates.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {dates.slice(0, 12).map((d) => (
                <Chip
                  key={d}
                  label={d}
                  size="small"
                  variant={d === date ? 'filled' : 'outlined'}
                  color={d === date ? 'success' : 'default'}
                  onClick={() => setDate(d)}
                  sx={{ cursor: 'pointer', fontSize: '0.72rem' }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Feedback */}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Input table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20' }}>Data Entry</Typography>
          <Typography variant="caption" sx={{ color: '#888' }}>ppm NO₃-N = ppm NO₃⁻ × 0.226</Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1b5e20' }}>
                <TableCell align="center" sx={{ color: '#fff', fontWeight: 700, border: '1px solid #2e7d32', width: 130 }}>Fermenter</TableCell>
                <TableCell align="center" sx={{ color: '#fff', fontWeight: 700, border: '1px solid #2e7d32' }}>ppm NO₃⁻ (input)</TableCell>
                <TableCell align="center" sx={{ color: '#fff', fontWeight: 700, border: '1px solid #2e7d32' }}>ppm NO₃-N (calc)</TableCell>
                <TableCell align="center" sx={{ color: '#fff', fontWeight: 700, border: '1px solid #2e7d32', width: 160 }}>Remark</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {FERMENTERS.map((ferm, idx) => {
                const c = computed[idx];
                return (
                  <TableRow key={ferm} hover>
                    <TableCell align="center" sx={{ fontWeight: 700, border: '1px solid #e8f5e9', bgcolor: '#f9fafb' }}>
                      {ferm}
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #e8f5e9', p: 0.75 }}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ step: '0.01', min: 0 }}
                        value={rows[ferm]}
                        onChange={(e) => setRows((prev) => ({ ...prev, [ferm]: e.target.value }))}
                        placeholder="e.g. 82.5"
                        sx={{ width: 130 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #e8f5e9', fontWeight: 600, color: '#263238' }}>
                      {c.ppmNo3N != null ? c.ppmNo3N.toFixed(4) : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #e8f5e9' }}>
                      {c.remark ? (
                        <Chip
                          label={c.remark}
                          size="small"
                          color={remarkColor(c.remark) as any}
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                        />
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Action buttons */}
        <Box sx={{ p: 2, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleDelete}
            disabled={saving || !savedReport}
          >
            Delete Date
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#2e7d32' } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Paper>

      {/* Saved report preview */}
      {savedReport && savedReport.fermenters.some((f) => f.ppmNo3 != null) && (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20' }}>
              Saved Report — {savedReport.measurementDate}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Target: &lt;50 mg/L (Beneficial) · 50–200 mg/L (Moderate) · &gt;200 mg/L (Above range)
            </Typography>
          </Box>
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#2e7d32' }}>
                  {['Fermenter', 'ppm NO₃⁻', 'ppm NO₃-N', 'Target Range', 'Remark'].map((h) => (
                    <TableCell key={h} align="center" sx={{ color: '#fff', fontWeight: 700, border: '1px solid #388e3c' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {savedReport.fermenters
                  .filter((f) => f.ppmNo3 != null)
                  .map((f) => (
                    <TableRow key={f.fermenter} hover>
                      <TableCell align="center" sx={{ fontWeight: 700, border: '1px solid #c8e6c9' }}>{f.fermenter}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #c8e6c9' }}>
                        {f.ppmNo3 != null ? Number(f.ppmNo3).toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #c8e6c9', fontWeight: 600 }}>
                        {f.ppmNo3N != null ? Number(f.ppmNo3N).toFixed(4) : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #c8e6c9', color: '#546e7a', fontSize: '0.75rem' }}>
                        NO₃-N &lt;50 mg/L
                      </TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #c8e6c9' }}>
                        {f.remark ? (
                          <Chip
                            label={f.remark}
                            size="small"
                            color={remarkColor(f.remark) as any}
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                          />
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Legend */}
      <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f9fbe7', borderRadius: 2, border: '1px solid #dce775' }}>
        <Typography variant="caption" sx={{ color: '#555', display: 'block', fontWeight: 600, mb: 0.5 }}>Interpretation</Typography>
        <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block' }}>
          <strong>Beneficial (&lt;50 mg/L):</strong> "Safe, may be assimilated by microbes."
        </Typography>
        <Typography variant="caption" sx={{ color: '#e65100', display: 'block' }}>
          <strong>Above range (200–500 mg/L):</strong> "Strong inhibition of methane production."
        </Typography>
        <Typography variant="caption" sx={{ color: '#f57c00', display: 'block' }}>
          <strong>Moderate range (50–200 mg/L):</strong> "Can start competing with methanogenesis."
        </Typography>
      </Box>
    </Box>
  );
};

export default NitrateNForm;
